import { byId as departmentsById } from '../data/departments.js';
import { tierFor } from '../sim/standing.js';

/**
 * The placeholder dialogue box.
 *
 * It reads out the two facts that will matter when there is real dialogue here:
 * which department this person belongs to, and exactly where you stand with
 * them. Those numbers are the input to every line the thirteen will eventually
 * say, so putting them on screen now means the writing can be checked against a
 * system that already works rather than written against a guess.
 *
 * ---------------------------------------------------------------------------
 * TODO — THE THIRTEEN
 *
 * Replace `line` with a table keyed by the standing tiers in `standing.js`
 * (Hated / Resented / Wary / Neutral / Friendly / Trusted / Family), so the
 * same character greets you differently at -60 and at +60. That is the smallest
 * version of the feature and it should go in before anything branching.
 *
 * After that, in the order they are worth doing:
 *   1. Per-character lines, not per-department. Seven archetypes become
 *      thirteen people; a Kitchen line is not a Chef line.
 *   2. Topics: a short list of things this character will talk about right now,
 *      gated on standing and on what has happened today (a graded BEO, a
 *      write-up, who else you have been seen with).
 *   3. Choices with a cost. A line that pleases one department should be able
 *      to be the same line that costs you another — the rivalry matrix is
 *      already sitting there waiting for it, and `standing.apply()` takes a
 *      reason string for exactly this.
 *   4. Barks: lines said unprompted as they pass you in the corridor, which is
 *      where most of Bully's character actually lives.
 *
 * Nothing in here should need new systems. It needs writing.
 * ---------------------------------------------------------------------------
 */
function dealMarkup(deal) {
  if (deal.closed) return `<p class="dialogue-closed">${deal.closed}</p>`;
  const rows = deal.options
    .map(
      (option, index) => `<div class="deal" data-kind="${option.kind}" data-allowed="${
        option.allowed ? 'yes' : 'no'
      }">
        <span class="deal-key">[${index + 1}]</span>
        <span class="deal-kind">${option.kind.toUpperCase()}</span>
        <span class="deal-detail">${option.allowed ? option.detail : option.reason}</span>
      </div>`
    )
    .join('');
  return `<div class="dialogue-deal">
      <h5>${deal.title}</h5>
      <p class="dialogue-short">${deal.shortfall}</p>
      ${rows}
    </div>`;
}

/**
 * Something being said *to* you, rather than offered.
 *
 * The one shape in this box that is not a negotiation: no keys, no options, no
 * price. Your manager crossing the property to tell you what is on your card is
 * not a deal, and drawing it as one would put a [1] next to a sentence there is
 * nothing to press.
 *
 * The facts are listed under the lines rather than written into them on
 * purpose. The lines are placeholder dialogue and will be replaced; the dates
 * and the numbers underneath are the warning, and they have to survive whoever
 * writes the words.
 */
function noticeMarkup(notice) {
  return `<div class="dialogue-notice">
      <span class="dialogue-strikes">${notice.kicker}</span>
      ${notice.lines.map((line) => `<p>${line}</p>`).join('')}
      <ul class="dialogue-facts">${notice.facts
        .map((fact) => `<li>${fact}</li>`)
        .join('')}</ul>
    </div>`;
}

export function createDialogue({ parent, standing }) {
  const root = document.createElement('div');
  root.className = 'dialogue';
  root.hidden = true;
  parent.append(root);

  return {
    root,
    get open() {
      return !root.hidden;
    },
    close() {
      root.hidden = true;
    },

    /**
     * `deal` is the gatekeeper menu: which shortfall is being discussed and
     * what this person will do about it.
     *
     * Rendered into the same box as the placeholder line rather than as its own
     * modal, and it does not pause anything. At the Front Desk the four second
     * uniform clock is running while you read it, which is the point — dithering
     * in the lobby is supposed to cost you.
     */
    show(npc, deal = null, notice = null, line = null) {
      /*
       * Not everybody you can talk to works here, and the ones who do not have
       * no standing to show.
       *
       * A guest's department is `guests`, which is deliberately not one of the
       * seven — "a guest is not a special case anywhere in the logic", says the
       * top of `guests.js`, and it means there is no row for them in the
       * departments table. This box used to read the row without looking, so
       * pressing E at a guest with nothing else in reach took the frame loop
       * with it. They get a name and a line and no number, which is the truth:
       * there is nothing to be owed by somebody who is checking out on Sunday.
       */
      const department = departmentsById[npc.data.department] ?? null;
      const colour = department
        ? `#${department.colour.toString(16).padStart(6, '0')}`
        : '#9aa2b4';
      const standingBlock = () => {
        const value = standing.get(department.id);
        const tier = tierFor(value);
        return `
        <div class="dialogue-standing">
          <span>Standing with ${department.name}</span>
          <b data-tone="${tier.tone}">${Math.round(value)} · ${tier.label}</b>
        </div>
        <p class="dialogue-note">${department.blurb}</p>`;
      };

      root.innerHTML = `
        <div class="dialogue-who">
          <b style="color:${colour}">${npc.data.name}</b>
          <span>${department ? department.name : npc.data.affiliation ?? 'Guest'}</span>
        </div>
        ${notice ? '' : `<p class="dialogue-line">${line ?? npc.data.line ?? '…'}</p>`}
        ${notice ? noticeMarkup(notice) : ''}
        ${department ? standingBlock() : ''}
        ${deal ? dealMarkup(deal) : ''}
        <div class="dialogue-hint">[E] leave</div>`;
      root.hidden = false;
    },
  };
}
